import React, { useEffect, useMemo, useState } from "react";
import { Toaster } from "../../../common/Toaster/Toaster";
import { Modal } from "../../../common/comms/Modal";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { coachLockedOut, statusOptionsFor } from "./bookingAuthority";
import { normaliseStatus, requiresNote, statusTone } from "./bookingStatus";
import { formatShortDate, formatSlotRange } from "./slotGrid";

/**
 * Recording what became of one call.
 *
 * The dialog shell (Escape, outside click, focus, scroll lock) is the shared
 * `common/comms/Modal`; this component is only what goes inside it — one
 * dropdown, an optional note, an async submit.
 *
 * Two things it deliberately does not do. It **does not call the API**: the
 * parent passes `onSubmit`, so on a refusal the modal stays open with the
 * server's own wording visible and the typed note intact. And it **does not
 * hide options it cannot offer** — a status the viewer may set but not yet, or
 * one the account has no id for, is rendered disabled with its reason, because
 * an option that silently vanishes is indistinguishable from one that was never
 * permitted, and gets debugged as a permission bug.
 *
 * @param {Object} props
 * @param {Object|null} props.booking - The call being edited. Null closes it.
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} props.authority
 * @param {Map<string, { id: string, title: string }>} props.statusIndex
 * @param {boolean} [props.saving] - A write is in flight.
 * @param {(targetToken: string, reason: string) => Promise<void>} props.onSubmit
 * @param {() => void} props.onClose
 */
export const BookingStatusModal = ({
  booking,
  authority,
  statusIndex,
  saving = false,
  onSubmit,
  onClose,
}) => {
  const [token, setToken] = useState("");
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isOpen = Boolean(booking);

  // Re-read the clock on every open rather than once per mount: the timing gate
  // is the whole reason a previously-disabled option becomes available, and a
  // `now` frozen at mount would keep saying "the call hasn't ended" after it
  // has. Recomputed per render is cheap and always current.
  const options = useMemo(
    () =>
      isOpen
        ? statusOptionsFor({ authority, booking, statusIndex, now: new Date() })
        : [],
    [isOpen, authority, booking, statusIndex]
  );

  const locked = isOpen && coachLockedOut(authority, normaliseStatus(booking?.booking_status));
  const noteRequired = requiresNote(token);

  useEffect(() => {
    // Clear the form whenever a different call is opened, so a note typed for
    // one booking cannot follow the next one.
    setToken("");
    setReason("");
    setErrorMessage("");
  }, [booking?.id]);

  if (!isOpen) return null;

  const flashError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 4000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      flashError("Pick a status first.");
      return;
    }
    if (noteRequired && !reason.trim()) {
      flashError("Add a note saying what happened.");
      return;
    }
    // Re-check against a fresh clock at the moment of submit: a modal left
    // open across the end of a call must not send a write the API has started
    // refusing — and, just as
    // importantly, one left open *until* a call ends should go through.
    const live = statusOptionsFor({
      authority,
      booking,
      statusIndex,
      now: new Date(),
    }).find((option) => option.token === token);

    if (!live || live.disabled) {
      flashError(live?.reason || "That status can't be set on this call.");
      return;
    }

    try {
      await onSubmit(token, reason);
      onClose();
    } catch (err) {
      flashError(err?.message || "That didn't work. Try again.");
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-primary/40";

  const clientName = booking.user_name || "this client";
  const when = `${formatShortDate(booking.startsAt || booking.start_time)} · ${formatSlotRange(
    booking
  )}`;

  return (
    <Modal isOpen onClose={onClose} title="Set status" ariaLabel="Set booking status">
        {errorMessage ? <Toaster message={errorMessage} type="error" /> : null}

        {/*
          The call, spelled out. The decision is about one specific appointment
          and the agenda row it came from is now behind an overlay, so the
          who/when/what has to be restated here or it is being made blind.
        */}
        <div className="rounded-lg bg-light-gray/50 px-3 py-2.5">
          <p className="text-sm font-semibold text-bc-text">{clientName}</p>
          <p className="mt-0.5 text-xs text-bc-soft">{when}</p>
          <div className="mt-2">
            <StatusBadge tone={statusTone(booking.booking_status)}>
              {booking.booking_status || "No status yet"}
            </StatusBadge>
          </div>
        </div>

        {locked ? (
          /*
            The once-only rule. A coach records an outcome once; a second change
            is their manager's to make, and the product decision is that this
            happens by talking to them rather than through a request queue here.
            The manager is not named because `/get-current-employee` does not
            carry the reporting line — only `/fetch-employees` does, and one
            sentence of copy is not worth that request.
          */
          <>
            <p className="mt-4 text-sm text-bc-text-2">
              You've already set the status on this call. To change it again, ask
              your manager — they can update it from their own Bookings screen.
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[40px] rounded-lg border border-gray-200 px-4 text-sm font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
              >
                Close
              </button>
            </div>
          </>
        ) : options.length === 0 ? (
          <>
            <p className="mt-4 text-sm text-bc-text-2">
              There's nothing you can change on this call.
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[40px] rounded-lg border border-gray-200 px-4 text-sm font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-bc-text-2">
                New status
              </span>
              <select
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className={fieldClass}
              >
                <option value="">Choose a status</option>
                {options.map((option) => (
                  <option
                    key={option.token}
                    value={option.token}
                    disabled={option.disabled}
                  >
                    {/*
                      The reason rides on the label rather than sitting beside
                      the field: a native <select> gives a disabled option no
                      other way to explain itself, and the explanation is only
                      wanted at the moment somebody tries to pick it.
                    */}
                    {option.disabled
                      ? `${option.title} — ${option.reason}`
                      : option.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-bc-text-2">
                Note{" "}
                {noteRequired ? (
                  <span className="text-red-500" aria-hidden="true">*</span>
                ) : (
                  <span className="font-normal text-bc-soft">(optional)</span>
                )}
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required={noteRequired}
                aria-required={noteRequired}
                rows={3}
                placeholder="What happened?"
                className={fieldClass}
              />
              {/*
                The note is saved against this one status change, in the
                booking's history — it does not overwrite anything earlier.
              */}
              <span className="mt-1 block text-[11px] text-bc-soft">
                Saved with this status change in the call's history.
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[40px] rounded-lg border border-gray-200 px-4 text-sm font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="min-h-[40px] rounded-lg bg-orange-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
              >
                {saving ? "Saving…" : "Save status"}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};
